/**
 * Shared /auth/me refresh controller.
 *
 * Parallel desk JSON retries (prefetch + active tab) must not stampede
 * /auth/me. A failed me that finishes after a successful one must not clear
 * a still-valid session. Logout must invalidate so a late me cannot
 * applySession a user back.
 */

/**
 * @param {{
 *   fetchMe: () => Promise<unknown>,
 *   applySession: (payload: unknown | null) => void,
 * }} opts
 */
export function createSessionRefresh({ fetchMe, applySession }) {
  let inFlight = null;
  let generation = 0;

  async function runRefresh() {
    const gen = generation;
    try {
      const me = await fetchMe();
      if (gen !== generation) return null;
      applySession(me);
      return me;
    } catch {
      if (gen !== generation) return null;
      applySession(null);
      return null;
    }
  }

  return {
    /**
     * Deduped refresh — concurrent callers share one in-flight Promise.
     * @returns {Promise<unknown | null>}
     */
    refresh() {
      if (!inFlight) {
        const p = runRefresh().finally(() => {
          if (inFlight === p) inFlight = null;
        });
        inFlight = p;
      }
      return inFlight;
    },

    /**
     * Bump generation and drop the in-flight handle so a late /me result is
     * ignored and the next refresh() starts a fresh request.
     */
    invalidate() {
      generation += 1;
      inFlight = null;
    },

    /** @returns {number} */
    getGeneration() {
      return generation;
    },
  };
}

/**
 * Whether an active-tab desk JSON failure should stamp loadFailedByKey /
 * setError. Never permanent-fail after a successful body was already stored
 * (e.g. prefetch won the race while a cancelled retry 401'd).
 *
 * @param {{ cancelled?: boolean, alreadyHasData?: boolean }} opts
 */
export function shouldCommitDeskLoadFailure({ cancelled = false, alreadyHasData = false } = {}) {
  return !cancelled && !alreadyHasData;
}

/**
 * Whether a sticky load `error` should block the dashboard view.
 * Prefer already-stored JSON for the active tab over an error screen
 * (prefetch may have the body while a late active-tab 401 stamped error).
 *
 * @param {{ error?: unknown, hasStoredData?: boolean }} opts
 */
export function shouldBlockOnDeskLoadError({ error = null, hasStoredData = false } = {}) {
  return Boolean(error) && !hasStoredData;
}

/**
 * Whether commitDeskJson should clear sticky `error` / `loading` for the
 * active view after storing a key's payload.
 *
 * Only for normal JSON dashboard tabs whose load key matches the commit.
 * Well detail remaps `activeKey` to `'wells'`, but loading/error there are
 * driven by dealPackage settle — a later wells.json (or other) prefetch must
 * not clear that gate. Shell routes (overview/package) are likewise excluded.
 *
 * @param {{
 *   committedKey?: string,
 *   activeKey?: string,
 *   isJsonDashboardTab?: boolean,
 * }} opts
 */
export function shouldClearStickyDeskLoadOnCommit({
  committedKey = '',
  activeKey = '',
  isJsonDashboardTab = false,
} = {}) {
  if (!isJsonDashboardTab) return false;
  return Boolean(committedKey) && committedKey === activeKey;
}
